import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { setCachedEventsCount } from 'composables/useEventsCountCache.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export const useEventsStore = defineStore('events', {
  state: () => ({
    events: [],
    fetchedAt: null,
    loading: false,
    error: null,
    layout: localStorage.getItem('events_layout') || 'grid',
    newFormData: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,

    totalActive: (state) => state.events.filter((e) => e.active).length,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid && this.events.length > 0) return
      await this.fetchEvents()
    },

    async fetchEvents() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/events', {}, false)
        if (!res.js_error && res.response.success) {
          this.events = res.response.data.map((e) => ({
            ...e,
            tempPublic: e.is_public,
            _uid: crypto.randomUUID(),
          }))
          this.fetchedAt = Date.now()
          setCachedEventsCount(this.events.length)
        } else {
          this.error = 'Failed to load events.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    setLayout(layout) {
      this.layout = layout
      localStorage.setItem('events_layout', layout)
    },

    updateEvent(publicKey, patch) {
      const idx = this.events.findIndex((e) => e.id === publicKey)
      if (idx !== -1) Object.assign(this.events[idx], patch)
    },

    addEvent(event) {
      this.events.unshift({ ...event, tempPublic: event.is_public, _uid: crypto.randomUUID() })
    },

    removeEvent(publicKey) {
      this.events = this.events.filter((e) => e.id !== publicKey)
    },

    async fetchNewFormData() {
      if (this.newFormData) return this.newFormData

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', '/api/v1/events/new_form_data', {}, false)
      if (!res.js_error && res.response.success) {
        this.newFormData = res.response.data
      }

      return this.newFormData
    },

    invalidate() {
      this.fetchedAt = null
    },

    invalidateNewFormData() {
      this.newFormData = null
    },
  },
})
